/**
 * The aim of a throw (weapons thrown by hand, Weapon.lob; author's rule): while it is charged, the dotted aim follows
 * the start of the arc for that power, as on the GDD's mockups. Without the wind (the player still reads it), up to
 * ARC_TICKS of flight or the first solid ground.
 */
import { STATE_LIQUID } from '@invaders/sim';
import { launchVelocity, projectileGravity, unitCenter, PFP, PFP_SHIFT, type GameState, type Unit } from '@invaders/game';
import type { Camera } from './camera';
import { toScreen } from './draw-units';

/** Ticks of flight shown: at full power, nearly up to the top of the arc. */
const ARC_TICKS = 45;
/** Cells between two dots. */
const DOT_SPACING = 3;

/** The points (cells) of the start of the flight of `weapon` thrown by `unit` along `angle` with `power`. */
export function throwArc(state: GameState, unit: Unit, weapon: number, angle: number, power: number): number[] {
  const { w, h, data } = state.world.physique;
  const matState = state.world.rules.materials.state;
  const [cx, cy] = unitCenter(unit);
  let x = (cx << PFP_SHIFT) + (PFP >> 1);
  let y = (cy << PFP_SHIFT) + (PFP >> 1);
  const [vx, vy0] = launchVelocity(weapon, angle, power);
  let vy = vy0;
  const g = projectileGravity(state, weapon);
  const points = [x / PFP, y / PFP];
  for (let t = 0; t < ARC_TICKS; t++) {
    vy += g;
    x += vx;
    y += vy;
    const gx = x >> PFP_SHIFT;
    const gy = y >> PFP_SHIFT;
    if (gx < 0 || gx >= w || gy >= h) break;
    points.push(x / PFP, y / PFP);
    if (gy >= 0 && matState[data[gy * w + gx]] < STATE_LIQUID) break;
  }
  return points;
}

/** Dots along the arc, every DOT_SPACING cells, in the colour of the power gauge, fading toward the end. */
export function drawThrowArc(ctx: CanvasRenderingContext2D, cam: Camera, points: readonly number[], power: number): void {
  const p = Math.min(1, Math.max(0, power / 100));
  const size = Math.max(2, Math.round(cam.scale * 0.9));
  let length = 0;
  for (let i = 2; i < points.length; i += 2) length += Math.hypot(points[i] - points[i - 2], points[i + 1] - points[i - 1]);
  if (length === 0) return;
  ctx.fillStyle = `rgb(255, ${Math.round(210 - 170 * p)}, 40)`;
  let next = DOT_SPACING;
  let done = 0;
  for (let i = 2; i < points.length; i += 2) {
    const [x0, y0, x1, y1] = [points[i - 2], points[i - 1], points[i], points[i + 1]];
    const step = Math.hypot(x1 - x0, y1 - y0);
    while (step > 0 && next <= done + step) {
      const k = (next - done) / step;
      const [sx, sy] = toScreen(cam, x0 + (x1 - x0) * k, y0 + (y1 - y0) * k);
      ctx.globalAlpha = 1 - 0.7 * (next / length);
      ctx.fillRect(Math.round(sx - size / 2), Math.round(sy - size / 2), size, size);
      next += DOT_SPACING;
    }
    done += step;
  }
  ctx.globalAlpha = 1;
}
